import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { noop, isEqual } from 'lodash';

import isMobile from 'shared/utils/checkMobileDevice';
import { Icon, Label, FormFieldError } from 'shared/components';
import { ASTERISK, OPTIONAL, REQUIRED } from 'shared/components/_constants/label';
import requestsStatuses from 'modules/common/constants/requestsStatuses';

import theme from './Input.scss';

class Input extends Component {

  static propTypes = {
    testId: PropTypes.string.isRequired,
    intl: PropTypes.object.isRequired,
    isControlled: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.any,
    defaultValue: PropTypes.string,
    type: PropTypes.string,
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    className: PropTypes.string,
    rootClassName: PropTypes.string,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    labelSuffix: PropTypes.oneOfType([
      PropTypes.oneOf([ASTERISK, OPTIONAL, REQUIRED]),
      PropTypes.node,
    ]),
    suffix: PropTypes.node,
    prefix: PropTypes.node,
    placeholder: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.string,
    ]),
    iconName: PropTypes.string,
    iconPositionLeft: PropTypes.bool,
    iconPositionRtlNotReversed: PropTypes.bool,
    status: PropTypes.oneOf([
      requestsStatuses.default,
      requestsStatuses.failure,
      requestsStatuses.pending,
      requestsStatuses.success,
    ]),
    greenSuccessStatus: PropTypes.bool,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    mediaResized: PropTypes.bool,
    validatePattern: PropTypes.string,
    clearable: PropTypes.bool,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    autoFocus: PropTypes.bool,
    enableAutoComplete: PropTypes.bool,
    highlightTextOnError: PropTypes.bool,

    tooltipType: PropTypes.oneOf(['text', 'hint', 'intl']),
    tooltipTestId: PropTypes.string,
    tooltipMessage: PropTypes.object,
    tooltipSize: PropTypes.oneOf(['medium', 'large']),
    tooltipWidth: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    tooltipPlacement: PropTypes.string,
    inputMode: PropTypes.string,
    autoComplete: PropTypes.string,
  };

  static defaultProps = {
    isControlled: true,
    name: null,
    value: null,
    defaultValue: '',
    type: 'text',
    minLength: 0,
    maxLength: null,
    className: '',
    rootClassName: '',
    label: null,
    labelSuffix: null,
    suffix: null,
    prefix: null,
    placeholder: { defaultMessage: ' ', id: 'default' },
    iconName: '',
    iconPositionLeft: false,
    iconPositionRtlNotReversed: false,
    status: requestsStatuses.default,
    greenSuccessStatus: false,
    highlightTextOnError: false,
    size: 'small',
    mediaResized: false,
    validatePattern: '',
    clearable: true,
    disabled: false,
    required: false,
    onFocus: noop,
    onBlur: noop,
    autoFocus: false,
    enableAutoComplete: false,
    inputMode: undefined,
    autoComplete: 'on',

    tooltipType: 'intl',
    tooltipTestId: 'tooltip',
    tooltipMessage: null,
    tooltipSize: 'large',
    tooltipWidth: 'fit-content',
    tooltipPlacement: 'bottomStart',
  };

  constructor(props) {
    super(props);

    this.state = {
      focused: false,
      value: null,
    };
  }

  componentDidMount() {
    const input = this.input;
    const length = input && input.value.length;
    if (input && !isMobile()) input.setSelectionRange(length, length);

    if (this.props.autoFocus && input) {
      input.focus();
      this.setState({ focused: true }); //eslint-disable-line
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (nextProps.value !== this.props.value
      || nextProps.status !== this.props.status
      || !isEqual(nextProps.placeholder, this.props.placeholder)
      || nextProps.tooltipMessage !== this.props.tooltipMessage
      || nextProps.defaultValue !== this.props.defaultValue
      || nextProps.disabled !== this.props.disabled
      || nextProps.labelSuffix !== this.props.labelSuffix
      || nextState.value !== this.state.value
      || nextState.focused !== this.state.focused
    );
  }

  componentDidUpdate(prevProps) {
    if (!this.props.isControlled && !this.props.value && this.state.value && prevProps.value) {
      this.setState({ value: this.props.value }); //eslint-disable-line
    }
  }

  handleKeyPress = (e) => {
    e.persist();
    const regExp = new RegExp(this.props.validatePattern);
    const key = String.fromCharCode(e.charCode);

    if (!regExp.test(key) && (e.charCode !== 13)) {
      e.preventDefault();
    }
  };

  handleChange = (e) => {
    const { onChange, isControlled } = this.props;
    const value = e.target.value || '';

    if (!isControlled) this.setState({ value });
    onChange(value, e);
  };

  removeValue = (e) => {
    e.stopPropagation();
    this.input.value = '';

    this.handleFocus(e);
    this.handleChange(e);
  };

  handleFocus = (e) => {
    this.setState({ focused: true });

    if (this.input) {
      this.input.focus();
    }

    this.props.onFocus(e);
  };

  handleBlur = (e) => {
    this.setState({ focused: false });

    this.props.onBlur(e);
  };

  isDeleteIconVisible = () => {
    const {
      clearable,
      disabled,
      status,
      value,
      defaultValue,
    } = this.props;
    const fieldValue = value || defaultValue;

    return fieldValue && fieldValue.length !== 0 && clearable && !disabled && !(status === requestsStatuses.success);
  };

  fieldRenderer() {
    const {
      intl,
      testId,
      className,
      wrapperClassName,
      rootClassName,
      suffix,
      prefix,
      placeholder,
      iconName,
      iconPositionLeft,
      iconPositionRtlNotReversed,
      status,
      greenSuccessStatus,
      size,
      mediaResized,
      disabled,
      required,
      id,
      name,
      value,
      defaultValue,
      type,
      minLength,
      maxLength,
      enableAutoComplete,
      tooltipType,
      tooltipTestId,
      tooltipMessage,
      tooltipSize,
      tooltipWidth,
      tooltipPlacement,
      highlightTextOnError,
      ariaLabel,
      onClick,
      onKeyDown,
      inputMode,
      autoComplete,
    } = this.props;

    const { focused, value: stateValue } = this.state;

    const inputProps = {
      id: id || name,
      name,
      disabled,
      type,
      minLength,
      maxLength,
      className,
      inputMode,
      placeholder: placeholder && typeof (placeholder) === 'object' ? intl.formatMessage({ ...placeholder }) : placeholder,
      'aria-label': ariaLabel ? typeof (ariaLabel) === 'string' ? ariaLabel : intl.formatMessage(ariaLabel) : undefined,
      'aria-required': required,
      onChange: this.handleChange,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      onKeyPress: this.handleKeyPress,
      value: stateValue !== null ? stateValue : value || defaultValue,
      ref: (ref) => this.input = ref,
      autoComplete,
    };

    if (name) {
      inputProps.name = name;
      if (enableAutoComplete) inputProps.autoComplete = name;
    }

    const rootClasses = classnames({
      [theme.iconRight]: !!iconName,
      [theme.iconLeft]: iconPositionLeft,
      [theme.iconRtlNotReversed]: iconPositionRtlNotReversed,
      [theme.iconDelete]: this.isDeleteIconVisible(),
      [theme.disabled]: disabled,
      [theme.inputFocused]: focused,
      [theme.mediaResized]: mediaResized,
      [theme[status.toLowerCase()]]: status.toLowerCase(),
      [theme.highlightTextOnError]: highlightTextOnError && status.toLowerCase() === 'failure',
      [theme.greenSuccess]: greenSuccessStatus && (status === requestsStatuses.success),
      [theme[size]]: size,
    }, theme.root, rootClassName);

    return (
      <div className={ rootClasses } onClick={ onClick } onKeyDown={ onKeyDown }>
        <div className={ classnames(wrapperClassName, theme.input) }>

          <If condition={ prefix }>
            <div
              className={ theme.prefix }
              onClick={ this.handleFocus }
            >
              { prefix }
            </div>
          </If>

          { iconName && (
            <div
              className={ classnames(theme.iconContainer, {
                [theme.fullField]: stateValue || value || defaultValue,
              }) }
              onClick={ this.handleFocus }
            >
              <Icon name={ iconName } />
            </div>
          ) }

          <input
            style={ highlightTextOnError && status.toLowerCase() === 'failure' ? { color: '#E63C3C' } : {} }
            { ...inputProps }
            data-testid={ testId }
          />

          { greenSuccessStatus && (status === requestsStatuses.success) && (
            <div className={ theme.successIconContainer }>
              <Icon name="check" />
            </div>
          ) }

          { this.isDeleteIconVisible() && (
            <button
              type="button"
              className={ theme.deleteIconWrap }
              onClick={ this.removeValue }
              aria-label={ intl.formatMessage({ id: 'GeneralInputField.ClearButton.TilteAttribute', defaultValue: '** Clear input' }) }
            >
              <Icon
                name="circle-x-fill"
                className={ classnames(theme.deleteIcon, {
                  [theme[size]]: size,
                  [theme.mediaResized]: mediaResized,
                }) }
              />
            </button>
          ) }

          <If condition={ suffix }>
            <div
              className={ theme.suffix }
              onClick={ this.handleFocus }
            >
              { suffix }
            </div>
          </If>
        </div>

        <FormFieldError
          tooltipType={ tooltipType }
          testId={ tooltipTestId }
          width={ tooltipWidth }
          tooltipMessage={ tooltipMessage }
          tooltipSize={ tooltipSize }
          tooltipPlacement={ tooltipPlacement }
        />

      </div>
    );
  }

  render() {
    const { label, labelSuffix, id, name } = this.props;

    return (
      <Choose>
        <When condition={ label }>
          <div>
            <Label
              label={ label }
              className={ theme.label }
              labelSuffix={ labelSuffix }
              htmlFor={ id || name }
            />
            { this.fieldRenderer() }
          </div>
        </When>
        <Otherwise>
          { this.fieldRenderer() }
        </Otherwise>
      </Choose>
    );
  }
}

export default injectIntl(Input);
